import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './PrivacyPolicy.module.css';
import PublicSiteHeader from './PublicSiteHeader';

function AccordionToggleIcon({ expanded }) {
  return (
    <span className={styles.accordionIcon} aria-hidden>
      <svg
        className={styles.accordionIconSvg}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        {expanded ? (
          <path d="M5 12h14" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
        ) : (
          <>
            <path d="M12 5v14" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
            <path d="M5 12h14" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
          </>
        )}
      </svg>
    </span>
  );
}

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const accordionData = [
    {
      id: 'faq',
      title: 'Privacy in Plain Language (FAQ)',
      content: `Here's a simple breakdown of how Richuel protects your privacy and handles your data.

<br/><br/><strong>What info does Richuel collect?</strong>

<br/>* <strong>What you share:</strong> Name, email, household/career/financial details (income, expenses, goals), payment info (via processors).
<br/>* <strong>Automatically:</strong> Device info, IP address, usage (pages viewed, time spent), cookies for personalization/security.
<br/>* <strong>From others:</strong> Login providers or public sources (rarely).

<br/><br/><strong>How does Richuel use it?</strong>

<br/>* Deliver planning tools/scenarios, manage accounts, send updates/support.
<br/>* Improve features, prevent fraud, comply with laws.
<br/>* Marketing (opt out anytime).

<br/><br/><strong>Who does Richuel share it with?</strong>

<br/>* Service providers (hosting, payments, analytics)—they're contractually bound to protect it.
<br/>* Vetted partners (e.g., CFPs)—only with your explicit consent for referrals. We select based on experience/feedback.
<br/>* Affiliates, legal authorities (if required), or in business sales.
<br/>* No selling your data.

<br/><br/><strong>Can I control my data?</strong>

<br/>* Update/delete via account or email <strong>privacy@richuel.com</strong>.
<br/>* Opt out of marketing/cookies via links/settings.
<br/>* CCPA/California: Opt out of "sharing," access/delete rights.

<br/><br/><strong>How secure is it?</strong>

<br/>Richuel implements industry-leading technical, administrative, and physical safeguards to protect your data from unauthorized access, use, or disclosure. We use encryption, access controls, regular security audits, and continuous monitoring. In the unlikely event of a breach, we will notify affected users promptly as required by law.

<br/><br/><strong>Questions?</strong>

<br/><strong>privacy@richuel.com</strong> or <strong>team@richuel.com</strong>.`
    },
    {
      id: '1',
      title: '1. Who We Are and Scope',
      content: `* <strong>"Richuel," "we," "us," or "our"</strong> means Richuel, Inc. and its applicable affiliates.
<br/>* <strong>"Website"</strong> means any publicly accessible site we operate, including <strong>www.richuel.com</strong>.
<br/>* <strong>"Services"</strong> means our software-as-a-service financial planning platform, web and mobile applications, conversational tools, APIs, and any other products or services provided by Richuel.

<br/><br/>This Privacy Policy applies to information we collect:
<br/>* Through the Website and Services
<br/>* In email, text, chat, and other electronic messages between you and Richuel
<br/>* When you interact with our content, events, or marketing

<br/><br/>It does not apply to:
<br/>* Third-party websites, apps, or services that are not under our control
<br/>* Third-party providers (such as Certified Financial Planner™ professionals or other partners) acting under their own privacy policies`
    },
    {
      id: '2',
      title: '2. Information We Collect',
      content: `Richuel collects information to provide, improve, secure, and personalize the Services. We collect information in three main ways: (A) information you provide, (B) information collected automatically, and (C) information from other sources.

<br/><br/><strong>A. Information You Provide to Us</strong>

<br/>Depending on how you use the Services, you may provide:

<br/><br/><strong>Account Information</strong>
<br/>* Name, email address, password, date of birth, and other details used to create and manage your account.

<br/><br/><strong>Profile and Household Information</strong>
<br/>* Information about your household, career, income, expenses, assets, liabilities, goals, and financial or life scenarios you choose to discuss or enter into the Services.

<br/><br/><strong>Communications with Richuel</strong>
<br/>* Information you provide when you contact us, including email, in-app messages, or forms (e.g., name, email, phone number, and the content of your messages).

<br/><br/><strong>Payment Information</strong>
<br/>* When you subscribe to a paid plan or make a purchase, we may receive limited billing details such as your name, billing address, and transaction information.
<br/>* Payments are processed by third-party payment processors, and Richuel does not store or directly handle full payment card numbers.

<br/><br/><strong>Surveys, Feedback, and Research</strong>
<br/>* Responses and related information if you participate in surveys, research, interviews, or beta programs.

<br/><br/><strong>Events and Promotions</strong>
<br/>* Information you provide when you register for webinars, events, or promotional campaigns (such as name, email, organization, and any other details you choose to share).

<br/><br/><strong>Interactive Features and Public Content</strong>
<br/>* Content you choose to post in public or semi-public areas (for example, public chats, community forums, or social media pages). Public content may be visible to others and is not protected by this Privacy Policy.

<br/><br/><strong>Job Applications</strong>
<br/>* If you apply for a role at Richuel, we collect the information you submit (such as résumé/CV, contact details, and any additional materials) to evaluate and process your application.

<br/><br/><strong>B. Information We Collect Automatically</strong>

<br/>When you use the Website or Services, certain information is collected automatically to help operate, secure, and improve the experience. This may include:

<br/><br/><strong>Usage and Device Information</strong>
<br/>* IP address and approximate location derived from it
<br/>* Device type, operating system, browser type, language, and settings
<br/>* Unique identifiers (e.g., cookie IDs, mobile advertising IDs, device IDs)
<br/>* Pages viewed, clicks, scrolling, time spent, and other interactions
<br/>* Referring and exit pages, and the sequence of pages you visit
<br/>* Frequency and duration of use across sessions and devices

<br/><br/><strong>Cookies and Similar Technologies</strong>
<br/>* Cookies, pixel tags (web beacons), local storage, and other technologies to:
<br/>* Remember preferences and settings
<br/>* Keep you signed in
<br/>* Understand usage patterns and performance
<br/>* Support security and fraud prevention
<br/>* Provide and measure marketing and analytics
<br/>* You can manage cookie preferences via your browser and, where available, via our on-site cookie settings.

<br/><br/><strong>Analytics and Measurement</strong>
<br/>* We use analytics providers and similar partners to understand usage, performance, and engagement with the Services. These providers may use their own technologies to collect information on our behalf.

<br/><br/><strong>C. Information from Other Sources</strong>

<br/>We may receive information about you from third parties, such as:
<br/>* Single sign-on or identity providers (e.g., if you log in with a third-party service)
<br/>* Marketing, analytics, or referral partners
<br/>* Publicly available sources (e.g., professional profiles)

<br/><br/>The information we receive depends on what those third parties are allowed to share based on their policies and your settings.`
    },
    {
      id: '3',
      title: '3. How We Use Your Information',
      content: `Richuel uses your information for the following purposes:

<br/><br/><strong>A. To Provide and Maintain the Services</strong>

<br/>* Create and manage your account
<br/>* Deliver interactive financial planning tools, projections, and scenarios
<br/>* Provide support, troubleshooting, and responses to your requests
<br/>* Process payments and manage subscriptions through third-party processors
<br/>* Verify your identity and maintain the security and integrity of the Services

<br/><br/><strong>B. To Improve, Personalize, and Develop the Services</strong>

<br/>* Analyze usage and performance to improve features and usability
<br/>* Personalize content, scenarios, or recommendations based on your inputs and usage
<br/>* Conduct research, testing, and development of new features and offerings
<br/>* Generate de-identified or aggregated insights for product and business analysis

<br/><br/><strong>C. To Communicate with You</strong>

<br/>* Send service-related notices (e.g., account changes, security alerts, and feature updates)
<br/>* Respond to your questions, feedback, or support requests
<br/>* Provide onboarding, educational resources, and product guidance

<br/><br/><strong>D. For Marketing and Promotions (Where Permitted)</strong>

<br/>* Send emails, in-product messages, or notifications about new features, content, events, or promotions related to Richuel or our partners, in accordance with applicable law
<br/>* Measure the effectiveness of marketing campaigns and communications

<br/><br/>You can opt out of certain marketing communications at any time, as described in Section 7.

<br/><br/><strong>E. To Support Safety, Security, and Legal Obligations</strong>

<br/>* Monitor, prevent, and detect fraud, abuse, and security incidents
<br/>* Enforce our Terms of Service and other policies
<br/>* Comply with legal and regulatory obligations, and respond to lawful requests and legal processes
<br/>* Protect the rights, property, and safety of Richuel, our users, and others

<br/><br/><strong>F. With Your Consent</strong>

<br/>We may use your information for additional purposes that we explain at the time of collection and for which we obtain your consent, where required.`
    },
    {
      id: '4',
      title: '4. How We Share Your Information',
      content: `Richuel does not sell your personal information in the common understanding of that term. Where applicable laws define "sale" or "sharing" more broadly (for example, to include certain analytics or advertising activities), additional information and choices are described in Section 9.

<br/><br/>We share information with third parties in the following circumstances:

<br/><br/><strong>A. Service Providers</strong>

<br/>We work with carefully selected service providers who perform services on our behalf, such as:
<br/>* Hosting and infrastructure
<br/>* Customer support and communications tools
<br/>* Payment processing
<br/>* Analytics, metrics, and error monitoring
<br/>* Security, fraud prevention, and threat detection
<br/>* Marketing and email tools

<br/><br/>These providers are allowed to access your information only as needed to perform services for us and are required to protect it in a manner consistent with this Privacy Policy and applicable law.

<br/><br/><strong>B. Vetted Advisors and Partner Providers</strong>

<br/>Richuel may introduce or refer you to third-party professionals and services ("Partner Providers"), including:
<br/>* Certified Financial Planner™ professionals and other advisers who may be registered investment advisers or associated with registered investment adviser firms
<br/>* Financial coaches, therapists, or other wellness professionals
<br/>* Financial institutions, platforms, and other financial or professional service providers

<br/><br/>Richuel maintains a <strong>curated and vetted network</strong> of Partner Providers, using commercially reasonable criteria such as:
<br/>* Professional experience, training, and credentials
<br/>* Regulatory or professional standing, where applicable
<br/>* Ongoing client and peer feedback
<br/>* Alignment with Richuel's standards for quality and user experience

<br/><br/>* Richuel will only share your personal information with Partner Providers with your explicit consent, such as when you request a referral, introduction, or scheduling connection.
<br/>* The information shared may include relevant details about your goals and situation (for example, high-level financial information, life stage, or areas where you seek guidance) to support a more effective engagement.
<br/>* Once shared, your information will be handled by the Partner Provider according to their own privacy notices and regulatory obligations, which may differ from ours. You should review those policies before engaging.

<br/><br/>Richuel may receive compensation or other benefits from Partner Providers in connection with referrals or collaborations, and will disclose such relationships as required by law. You are always free to choose whether to work with a Partner Provider, and any engagement is between you and that provider.

<br/><br/><strong>C. Business Partners and Affiliates</strong>

<br/>We may share information with:
<br/>* Affiliates that are owned or controlled by Richuel, or under common ownership, for purposes consistent with this Policy
<br/>* Business partners with whom we jointly offer content, events, or services, where you have chosen to interact with or receive such offerings

<br/><br/>Such parties must handle your information in a way that is at least as protective as this Privacy Policy or under comparable safeguards.

<br/><br/><strong>D. Legal, Safety, and Compliance</strong>

<br/>We may disclose information if we believe it is reasonably necessary to:
<br/>* Comply with applicable laws, regulations, legal processes, or lawful requests
<br/>* Protect the rights, property, or safety of Richuel, our users, or others
<br/>* Enforce our Terms of Service or other agreements
<br/>* Detect, prevent, or address fraud, security, or technical issues

<br/><br/><strong>E. Business Transfers</strong>

<br/>If Richuel is involved in a merger, acquisition, financing, reorganization, sale of assets, bankruptcy, or similar transaction, your information may be transferred as part of that transaction, subject to appropriate safeguards and any applicable legal requirements.

<br/><br/><strong>F. De-Identified and Aggregated Information</strong>

<br/>We may use and share de-identified or aggregated information (which cannot reasonably be used to identify you) for research, analytics, and other lawful purposes not limited by this Privacy Policy.`
    },
    {
      id: '5',
      title: '5. International Data Transfers',
      content: `Richuel is based in the United States and may process and store information in the U.S. and other countries that may have different data protection laws than those in your jurisdiction.

<br/><br/>Where required by law, we implement appropriate safeguards—such as contractual protections—to help ensure that your information remains protected when it is transferred across borders.`
    },
    {
      id: '6',
      title: '6. Data Retention',
      content: `Richuel retains personal information for as long as necessary to:
<br/>* Provide and maintain the Services
<br/>* Comply with legal, regulatory, and accounting obligations
<br/>* Resolve disputes and enforce agreements
<br/>* Support legitimate business needs (such as security, fraud prevention, and analytics)

<br/><br/>When information is no longer required for these purposes, we will delete, anonymize, or otherwise handle it in accordance with applicable laws and our data retention policies.`
    },
    {
      id: '7',
      title: '7. Your Choices',
      content: `Richuel aims to give you meaningful choices about your information. Your options may include:

<br/><br/><strong>A. Account and Profile</strong>
<br/>* You may access and update certain account or profile information directly within the Services.
<br/>* If you wish to deactivate your account, you can contact us using the information in Section 13.

<br/><br/><strong>B. Marketing Communications</strong>
<br/>* You may opt out of marketing emails by using the "unsubscribe" link included in those messages.
<br/>* Even if you opt out of marketing, you may still receive transactional or service-related messages (such as security alerts, account notices, or updates required by law).

<br/><br/><strong>C. Cookies and Tracking Technologies</strong>
<br/>* Most browsers allow you to control cookies, including blocking or deleting them; doing so may affect functionality.
<br/>* Where provided, you can adjust your preferences via our cookie settings or cookie banner.

<br/><br/><strong>D. Mobile and Device Settings</strong>
<br/>* You may disable push notifications or location access through your device or app settings. This may limit certain features but will not prevent you from using the core Services.

<br/><br/><strong>E. "Do Not Track" and Global Privacy Controls</strong>
<br/>* Some browsers offer "Do Not Track" signals; our Services currently do not respond to all such signals.
<br/>* Where required by applicable law, Richuel honors legally recognized browser-based opt-out signals, such as Global Privacy Control, in connection with certain data uses (for example, targeted advertising).`
    },
    {
      id: '8',
      title: '8. Security',
      content: `Richuel takes the protection of your personal information seriously and implements industry-leading administrative, technical, and physical safeguards designed to protect your data against unauthorized access, use, disclosure, alteration, or destruction. These measures include:

<br/>* Data encryption in transit and at rest
<br/>* Strict access controls and multi-factor authentication
<br/>* Regular security audits, penetration testing, and vulnerability assessments
<br/>* Continuous monitoring and threat detection systems
<br/>* Employee training on data protection best practices
<br/>* Contracts requiring third-party service providers to meet our security standards

<br/><br/>While we work tirelessly to protect your information, no method of transmission over the internet or method of electronic storage can be guaranteed to be completely secure. You use the Services and provide information at your own risk. In the event of a security incident that may affect your personal information, Richuel will notify you promptly in accordance with applicable laws and regulations.`
    },
    {
      id: '9',
      title: '9. Region-Specific Rights (Including California)',
      content: `Depending on where you live, you may have additional rights regarding your personal information under local laws (such as the California Consumer Privacy Act (CCPA), the California Privacy Rights Act (CPRA), or other data protection laws).

<br/><br/><strong>A. California Residents</strong>

<br/>If you are a California resident, you may have rights to:
<br/>* Know the categories and specific pieces of personal information collected, used, disclosed, or "sold"/"shared"
<br/>* Delete certain personal information
<br/>* Correct inaccurate personal information
<br/>* Opt out of the "sale" or "sharing" of personal information for cross-context behavioral advertising, as defined by California law
<br/>* Limit the use and disclosure of certain sensitive information, where applicable
<br/>* Not be discriminated against for exercising your privacy rights

<br/><br/>Richuel may work with analytics and advertising partners that use cookies and similar technologies which could be considered a "sale" or "sharing" under California law. You can:
<br/>* Use available cookie and tracking controls
<br/>* Click any "Do Not Sell or Share My Personal Information" link we provide, if applicable
<br/>* Contact us at <strong>privacy@richuel.com</strong> to exercise these rights

<br/><br/>Additional details about categories of information, purposes, and disclosures can be mapped to the sections above describing what we collect, how we use it, and how we share it.

<br/><br/><strong>B. Other Region-Specific Rights</strong>

<br/>If you are in a jurisdiction that grants you additional privacy rights (for example, certain access, deletion, portability, or objection rights), you may exercise those rights by contacting us at <strong>privacy@richuel.com</strong>. We will verify and respond to your request as required by applicable law.`
    },
    {
      id: '10',
      title: "10. Children's Privacy",
      content: `The Services are not directed to children, and Richuel does not knowingly collect personal information from children where parental consent is required. If we learn that we have collected such information without appropriate consent, we will take reasonable steps to delete it.`
    },
    {
      id: '11',
      title: '11. Third-Party Websites and Services',
      content: `The Services may contain links to third-party websites, apps, or services, including those of Partner Providers. Those third parties have their own privacy policies and practices, which may differ from ours.

<br/><br/>Richuel is not responsible for the privacy or security practices of third-party sites or services. You should review their policies before providing any information or using their services.`
    },
    {
      id: '12',
      title: '12. Changes to This Privacy Policy',
      content: `Richuel may update this Privacy Policy from time to time. The "Last Updated" date at the top indicates when changes were last made.

<br/><br/>If we make material changes, we will provide notice as required by applicable law (for example, by posting a notice on the Website, within the Services, or by email). Your continued use of the Services after changes take effect indicates your acceptance of the updated Privacy Policy.`
    },
    {
      id: '13',
      title: '13. Contact Us',
      content: `If you have questions, requests, or complaints about this Privacy Policy or our privacy practices, you can contact us at:

<br/>* <strong>Email:</strong> <strong>privacy@richuel.com</strong> (for privacy rights and requests)
<br/>* <strong>Email:</strong> <strong>team@richuel.com</strong> (for general or product-related questions)
<br/>* <strong>Mailing Address:</strong> Richuel, Inc., 1900 Powell St., Suite 700, Emeryville, CA 94608`
    },
  ];

  return (
    <div className={styles.privacyPage}>
      <PublicSiteHeader linkLogoToHome onLogin={handleLogin} onSignup={handleSignup} />

      <main className={styles.mainContent} id="privacy-main" aria-labelledby="privacy-page-title">
        <div className={styles.contentWrapper}>
          <h1 id="privacy-page-title" className={styles.title}>
            Richuel Privacy Policy
          </h1>

          <div className={styles.policyContent}>
            <p className={`${styles.description} ${styles.descriptionMeta}`}>
              <strong>Last Updated: January 5, 2026</strong>
            </p>

            <p className={`${styles.description} ${styles.descriptionIntro}`}>
              At Richuel, privacy and security are core to how the Services are designed and delivered. This detailed Privacy Policy explains how we collect, use, share, and protect your information when you use our website, apps, and related services, and what choices and rights you have.
            </p>

            <p className={`${styles.description} ${styles.descriptionAgree}`}>
              By accessing or using the Services, you agree to this Privacy Policy and our{' '}
              <Link to="/terms" className={styles.inlineLink}>
                Terms of Service
              </Link>
              . If you do not agree, please do not use the Services.
            </p>

            <section className={styles.accordionSection} aria-labelledby="privacy-accordion-heading">
              <h2 id="privacy-accordion-heading" className={styles.accordionTitle}>
                Privacy Policy
              </h2>
              <div className={styles.accordionContainer} role="list">
                {accordionData.map((section, index) => {
                  const expanded = Boolean(expandedSections[section.id]);
                  return (
                    <div
                      key={section.id}
                      className={`${styles.accordionItem}${expanded ? ` ${styles.accordionItemOpen}` : ''}`}
                      role="listitem"
                      style={{ '--item-index': index }}
                    >
                      <button
                        type="button"
                        className={styles.accordionHeader}
                        onClick={() => toggleSection(section.id)}
                        aria-expanded={expanded}
                        aria-controls={`privacy-section-${section.id}`}
                        id={`privacy-trigger-${section.id}`}
                      >
                        <span className={styles.accordionQuestion}>{section.title}</span>
                        <AccordionToggleIcon expanded={expanded} />
                      </button>
                      <div
                        id={`privacy-section-${section.id}`}
                        className={`${styles.accordionPanel}${expanded ? ` ${styles.accordionPanelOpen}` : ''}`}
                        role="region"
                        aria-labelledby={`privacy-trigger-${section.id}`}
                        aria-hidden={!expanded}
                        inert={!expanded}
                      >
                        <div className={styles.accordionPanelInner}>
                          <div className={styles.accordionContent}>
                            <div
                              className={styles.accordionBody}
                              dangerouslySetInnerHTML={{ __html: section.content }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
